/**
 * Estado del switch de notificaciones del navegador (#277).
 *
 * Va con `useSyncExternalStore` contra el store de `browser-notifications` para que el sider y la
 * pantalla de Notificaciones muestren lo mismo sin pasar por un provider, y para que prender el
 * switch en una pestaña se vea en las otras.
 */
import { useCallback, useSyncExternalStore } from "react";
import {
  currentPermission,
  isEnabled,
  requestPermission,
  setEnabled,
  subscribeEnabled,
  subscribePermission,
  supportsNotifications,
} from "@/services/browser-notifications";

export interface BrowserNotificationsState {
  /** El navegador tiene Notification API. */
  supported: boolean;
  /** La preferencia del Operador, persistida. */
  enabled: boolean;
  permission: NotificationPermission;
  /** `denied` no se puede revertir desde la página: se cambia desde el candado. */
  blocked: boolean;
  /** Prende (pidiendo permiso si hace falta) o apaga. Devuelve el permiso resultante. */
  toggle: (next: boolean) => Promise<NotificationPermission>;
}

export function useBrowserNotifications(): BrowserNotificationsState {
  const enabled = useSyncExternalStore(
    subscribeEnabled,
    isEnabled,
    () => false,
  );
  const permission = useSyncExternalStore(
    subscribePermission,
    currentPermission,
    () => "denied" as NotificationPermission,
  );

  const toggle = useCallback(async (next: boolean) => {
    if (!next) {
      setEnabled(false);
      return currentPermission();
    }
    return requestPermission();
  }, []);

  return {
    supported: supportsNotifications(),
    // Un permiso revocado desde el candado deja la preferencia prendida pero sin efecto: el switch
    // tiene que mostrarse apagado, que es lo que el Operador realmente tiene.
    enabled: enabled && permission === "granted",
    permission,
    blocked: permission === "denied",
    toggle,
  };
}
